import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { ContactPageSEO } from '@/components/SEOHead';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success('Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.');
        setFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Mesaj gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      {/* SEO Head */}
      <ContactPageSEO />
      
      {/* Hero */}
      <section className="bg-emerald-800 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
            İletişim
          </h1>
          <p className="text-xl text-emerald-100 max-w-2xl mx-auto">
            Sorularınız için bize ulaşın, size yardımcı olmaktan mutluluk duyarız
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">Adres</h3>
                      <p className="text-slate-600 text-sm">
                        Altıntepe Mah. Galipbey Cad.<br />
                        Dış Kapı No: 119/A Dükkân No: 2<br />
                        Esen Apt., Maltepe/İSTANBUL
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">Telefon</h3>
                      <p className="text-slate-600 text-sm">
                        <a href="tel:+905053686091" className="hover:text-emerald-700">
                          +90 505 368 60 91
                        </a>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">Email</h3>
                      <p className="text-slate-600 text-sm">
                        <a href="mailto:tesiskirala@gmail.com" className="hover:text-emerald-700">
                          tesiskirala@gmail.com
                        </a><br />
                        <a href="mailto:avrasyasportifdestek@gmail.com" className="hover:text-emerald-700">
                          avrasyasportifdestek@gmail.com
                        </a>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">Çalışma Saatleri</h3>
                      <p className="text-slate-600 text-sm">
                        Pazartesi - Cuma: 09:00 - 18:00<br />
                        Cumartesi: 10:00 - 14:00<br />
                        Pazar: Kapalı
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Bize Mesaj Gönderin</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Ad Soyad</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Adınız Soyadınız"
                          required
                          className="mt-1"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="ornek@email.com"
                          required
                          className="mt-1"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="subject">Konu</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        placeholder="Mesajınızın konusu"
                        required
                        className="mt-1"
                        data-testid="contact-subject-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Mesaj</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        placeholder="Mesajınızı buraya yazın..."
                        rows={6}
                        required
                        className="mt-1"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={loading}
                      className="w-full md:w-auto bg-emerald-700 hover:bg-emerald-800 h-11 px-8"
                      data-testid="contact-submit-btn"
                    >
                      {loading ? (
                        'Gönderiliyor...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Gönder
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* FAQ */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Sık Sorulan Sorular</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">
                      Rezervasyonumu nasıl iptal edebilirim?
                    </h4>
                    <p className="text-sm text-slate-600">
                      Hesabım sayfasından rezervasyonlarınızı görüntüleyebilir ve iptal edebilirsiniz.
                    </p>
                  </div>
                  <hr className="border-slate-200" />
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">
                      Tesis sahibi olarak nasıl kayıt olabilirim?
                    </h4>
                    <p className="text-sm text-slate-600">
                      Kayıt olduktan sonra hesabım sayfasından "İşletmeci Ol" butonuna tıklayarak tesisinizi ekleyebilirsiniz.
                    </p>
                  </div>
                  <hr className="border-slate-200" />
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">
                      Ödeme güvenli mi?
                    </h4>
                    <p className="text-sm text-slate-600">
                      Evet, tüm ödemeleriniz SSL şifrelemesi ile korunmaktadır. Güvenli ödeme altyapımız yakında aktif olacaktır.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
